import { Component } from "react"
import { Shield, RefreshCw, Lock } from "lucide-react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import StripeCheckout from "./StripeCheckout"
import CostComparison from "./CostComparison"
import FeatureList from "./FeatureList"
import "./PremiumPage.css"
import posthog from "src/consent/posthog"
import { Crown, UserRound, CheckCircle2 } from "lucide-react"



interface PlanContentProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    showSuccess?: boolean
    showCancelled?: boolean
    onPaymentComplete?: () => void
    variant?: "page" | "modal"
}

interface PlanContentState {
    isPremium: boolean
    isLoggedIn: boolean
    featuresLoaded: boolean
    paid: boolean
    valuationsLast24h: number | null
    valuationsAllTime: number | null
    displayedLast24h: number
    showLifetimeCheckout: boolean
}


class PlanContent extends Component<PlanContentProps, PlanContentState> {
    constructor(props: PlanContentProps) {
        super(props)

        const features = props.stateHandler.get(StateConstants.USER_FEATURES)

        this.state = {
            isPremium: props.stateHandler.get(StateConstants.IS_PREMIUM) || false,
            isLoggedIn: props.stateHandler.get(StateConstants.IS_LOGGED_IN) || false,
            featuresLoaded: features !== undefined && features !== null,
            paid: false,
            valuationsLast24h: null,
            valuationsAllTime: null,
            displayedLast24h: 0,
            showLifetimeCheckout: false,
        }

        props.stateHandler.addListener(StateConstants.IS_PREMIUM, (isPremium: boolean) => {
            this.setState({ isPremium })
        })
        props.stateHandler.addListener(StateConstants.USER_FEATURES, () => {
            this.setState({ featuresLoaded: true })
        })
        props.stateHandler.addListener(StateConstants.IS_LOGGED_IN, (isLoggedIn: boolean) => {
            this.setState({ isLoggedIn })
        })
    }

    async componentDidMount() {
        try {
            const usage = await this.props.apiHandler.getGlobalUsage()
            if (usage) {
                this.setState({ valuationsLast24h: usage.last_24h, valuationsAllTime: usage.all_time })
                this.animateCountUp(usage.last_24h)
            }
        } catch (e) {
            // best effort
        }
    }

    animateCountUp(target: number) {
        if (target === 0) {
            this.setState({ displayedLast24h: 0 })
            return
        }
        const duration = 250
        const steps = Math.min(target, 40)
        const interval = duration / steps
        let current = 0
        const timer = setInterval(() => {
            current += Math.ceil(target / steps)
            if (current >= target) {
                current = target
                clearInterval(timer)
            }
            this.setState({ displayedLast24h: current })
        }, interval)
    }

    handleCheckoutComplete = (paymentIntentId: string) => {
        posthog?.capture("payment_completed", { payment_intent_id: paymentIntentId })
        this.setState({ paid: true })

        if (this.state.isLoggedIn) {
            this.claimAndFinish(paymentIntentId)
            return
        }

        this.props.stateHandler.set(StateConstants.PENDING_PAYMENT_INTENT_ID, paymentIntentId)
        localStorage.setItem("pendingPaymentIntentId", paymentIntentId)
        this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }

    async claimAndFinish(paymentIntentId: string) {
        try {
            await this.props.apiHandler.claimPayment(paymentIntentId)
        } catch {}
        if (this.props.onPaymentComplete) {
            this.props.onPaymentComplete()
        }
    }

    openSignup = () => {
        posthog?.capture("free_valuation_signup_clicked", { surface: this.props.variant || "page" })
        this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        const currentPropertyId = this.props.stateHandler.get(StateConstants.CURRENT_PROPERTY_ID)
        if (currentPropertyId) {
            this.props.stateHandler.set(StateConstants.PENDING_FREE_VALUATION_PROPERTY_ID, currentPropertyId)
        }

        if (this.state.isLoggedIn) {
            // The valuation page observes the pending property and performs the
            // unlock. Do not open the login modal for an already-authenticated user.
            return
        }

        this.props.stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "signup")
        this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }

    showLifetimeCheckout = () => {
        posthog?.capture("lifetime_checkout_selected", { surface: this.props.variant || "page" })
        this.setState({ showLifetimeCheckout: true })
    }

    renderModalChoiceCards() {
        const showLifetimeCheckout = this.state.showLifetimeCheckout && !this.state.isPremium && !this.state.paid

        if (showLifetimeCheckout) {
            return (
                <div className="plan-choice-shell plan-choice-shell--checkout">
                    <div className="premium-header premium-header--choices premium-header--checkout">
                        <h1 className="premium-page-title">Lifetime Access</h1>
                        <p className="premium-page-subtitle">One payment. Unlimited valuations, full comparables and complete price history.</p>
                    </div>

                    <div className="plan-choice-checkout-shell">
                        <StripeCheckout
                            stateHandler={this.props.stateHandler}
                            apiHandler={this.props.apiHandler}
                            isLoggedIn={this.state.isLoggedIn}
                            onComplete={this.handleCheckoutComplete}
                            onCancel={() => this.setState({ showLifetimeCheckout: false })}
                            onLoginRedirect={() => {
                                this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
                                this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                            }}
                        />
                    </div>
                </div>
            )
        }

        return (
            <div className="plan-choice-shell">
                <div className="premium-header premium-header--choices">
                    <h1 className="premium-page-title">Choose how to continue</h1>
                    <p className="premium-page-subtitle">Unlock the exact valuation and more insights.</p>
                </div>

                <div className="plan-choice-grid">
                    <article className="plan-choice-card">
                        <div className="plan-choice-icon">
                            <UserRound size={30} strokeWidth={1.9} />
                        </div>
                        <h2 className="plan-choice-title">Free account</h2>
                        <ul className="plan-choice-list">
                            <li><CheckCircle2 size={16} strokeWidth={2} /> Reveal 1 valuation</li>
                            <li><CheckCircle2 size={16} strokeWidth={2} /> No card needed</li>
                        </ul>
                        <button className="plan-choice-cta plan-choice-cta--free" onClick={this.openSignup}>
                            Reveal free valuation
                        </button>
                        <p className="plan-choice-footnote">Simple signup, no payment required.</p>
                    </article>

                    <article className="plan-choice-card plan-choice-card--lifetime">
                        <div className="plan-choice-icon plan-choice-icon--lifetime">
                            <Crown size={30} strokeWidth={1.9} />
                        </div>
                        <h2 className="plan-choice-title">Lifetime Access</h2>
                        <ul className="plan-choice-list">
                            <li><CheckCircle2 size={16} strokeWidth={2} /> Unlimited valuations</li>
                            <li><CheckCircle2 size={16} strokeWidth={2} /> All comparable sales</li>
                            <li><CheckCircle2 size={16} strokeWidth={2} /> Full price history</li>
                            <li><CheckCircle2 size={16} strokeWidth={2} /> €19 once</li>
                        </ul>
                        <button className="plan-choice-cta plan-choice-cta--lifetime" onClick={this.showLifetimeCheckout}>
                            Unlock forever
                        </button>
                        <p className="plan-choice-footnote plan-choice-footnote--placeholder" aria-hidden="true">Simple signup, no payment required.</p>
                    </article>
                </div>
            </div>
        )
    }

    render() {
        if ((this.props.variant || "page") === "modal") {
            return this.renderModalChoiceCards()
        }

        return (
            <>
                {this.props.showSuccess && (
                    <div className="premium-success-banner">
                        <span className="success-title">Welcome to Lifetime Access!</span>
                        <span className="success-subtitle">You now have full access to every feature, forever. Welcome.</span>
                    </div>
                )}
                {this.props.showCancelled && (
                    <div className="premium-cancelled-banner">
                        Checkout was cancelled. You can upgrade anytime.
                    </div>
                )}
                <div className="premium-header">
                    <h1 className="premium-page-title">Unlock Lifetime Access</h1>
                    <p className="premium-page-subtitle">Know what any home in Ireland is worth before you bid, list, or sell.</p>
                </div>
                {!(this.state.isPremium || this.state.paid) && (
                    <div className="plan-price-card">
                        <div className="plan-price-left">
                            <span className="plan-price-eyebrow">One-time payment</span>
                            <span className="plan-price-amount">€19</span>
                            <span className="plan-price-sub">Pay once · Unlock forever</span>
                        </div>
                        <ul className="plan-price-benefits">
                            <li><Shield size={16} strokeWidth={1.9} /> 30-day money-back guarantee</li>
                            <li><RefreshCw size={16} strokeWidth={1.9} /> No subscription</li>
                            <li><Lock size={16} strokeWidth={1.9} /> Secure checkout</li>
                        </ul>
                    </div>
                )}
                <CostComparison />
                <FeatureList />
                {(this.state.isPremium || this.state.paid) && (
                    <div className="stripe-checkout-lifetime-active">
                        <p className="stripe-checkout-lifetime-title">Lifetime Access Active</p>
                        <p className="stripe-checkout-lifetime-subtitle">You have full access to every feature, forever.</p>
                    </div>
                )}
                {!this.state.isPremium && !this.state.paid && (
                    <StripeCheckout
                        stateHandler={this.props.stateHandler}
                        apiHandler={this.props.apiHandler}
                        isLoggedIn={this.state.isLoggedIn}
                        onComplete={this.handleCheckoutComplete}
                        onCancel={() => this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)}
                        onLoginRedirect={() => {
                            this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
                            this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                        }}
                    />
                )}
                {this.state.valuationsLast24h !== null && (
                    <p className="premium-social-proof">
                        <span className="social-proof-pulse" aria-hidden />
                        <strong className="social-proof-count">{this.state.displayedLast24h.toLocaleString()}</strong> users have valued homes in the last 24 hours
                    </p>
                )}
            </>
        )
    }
}

export default PlanContent
